import moment, { Moment } from 'moment';
import { Country } from 'react-phone-number-input';
import {
  CaptchaMode,
  CashbackAccountOperationSource,
  CategoryType,
  CuisineType,
  DELIVERY_ZONE_GEOMETRY_TYPES,
  DeliveryMode,
  DeliveryProvider,
  DeliveryStatus,
  DeviceOS,
  DropoffAction,
  EntryType,
  GOOGLE_REVIEW_STATUS_TYPES,
  GiftCardRedemptionMode,
  LoginBonusKind,
  ModifierGroupType,
  ORDER_FULFILLMENT_STATUSES,
  ORDER_METHODS,
  ORDER_PAYMENT_STATES,
  ORDER_PAYMENT_STATUSES,
  OTP_DELIVERY_CHANNELS,
  OTP_VERIFICATION_FAILURE_REASON,
  OfferKindEnum,
  OfferTypeEnum,
  OnlineShopWebViewMessageTypes,
  PaymentGateway,
  PaymentMethod,
  PaymentMethodBrand,
  PaymentStatus,
  PushNotificationStatus,
  RestaurantSource,
  RoomServiceLocationType,
  SmartPricingRuleModifierType,
  TaxBehaviour,
} from './Constants';
import {
  COUPON_TYPES,
  EventHelperTypes,
  ORDERS_PAUSED_REASON,
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
} from './Constants';
import { FeedbackLabelTypes, RenderableErrorResponse } from './Types';
import { ReactElement, ReactNode } from 'react';
import { BasketLineItem, BasketLineItemModifier } from '../../store/types';
import * as stripeJs from '@stripe/stripe-js';
import { FeatureFlags } from '../../modules/mobile/exports/Enums';
import { PaymentData } from '@adyen/adyen-web';
import type { PaymentMethodSource } from '../helpers/checkoutPaymentMethod';

export type IMap<V, K extends string | number = string | number> = {
  [index in K]: V;
};

export interface ConfigInterface {
  primary_color: string | null;
  logo_url: string | null;
  // Absent from a config that predates the field — the native app's own copy included — which
  // reads as direct.
  captcha_mode?: CaptchaMode;
}

export interface UserInterface {
  id: number;
  first_name: string;
  last_name: string;
  created_at: string | null;
}

export interface IMobileAppBase {
  uuid: string;
  name: string;
  app_bundle_identifier: string;
  app_icon: string;
  asc_app_id: number;
  released: boolean;
}

export interface Franchise {
  id: number;
  name: string;
  slug: string;
  logo_url: string;
  logos: LogoInterface[];
  image_url: string | null;
  primary_color: string;
  country?: string;
  head_scripts: string | null;
  body_noscripts: string | null;
  facebook_pixel_id: string | null;
  google_analytics_id: string | null;
  google_ads_conversion_label_id: string | null;
  has_multiple_companies: boolean;
  companies: CompanyDetailsInterface[];
  mobile_app: IMobileAppBase | null;
  is_gift_cards_enabled: boolean;
  gift_cards_redemption_mode: GiftCardRedemptionMode;
  seo_data: FranchiseSeoData | null;
  /** Absolute shop URL resolved by the backend from the franchise's root domain or custom FQDN. */
  web_shop_url?: string | null;
}

export interface FranchiseSeoData {
  title: string | null;
  description: string | null;
}

export interface CompanyLocation {
  name?: null | string;
  day?: null | number;
  hour?: null | number;
  minute?: null | number;
  address: string;
  city: string;
  zip_code: null | string;
  country: Country;
  lat: number;
  lon: number;
}

// Per-region numeric caps (gift-card bounds, and others this client doesn't read yet —
// sales-commission, service-fee-transfer, fraud/SCA are backend-internal). Empty until
// the backend's region-command write path populates a region; every key is therefore
// optional and every consumer must fall back to its own pre-existing default.
export interface RegionLimitsInterface {
  gift_card?: {
    min: number;
    max: number;
  };
}

// The presentation/locale conventions for the company's region — see
// AddressHelpers/HelperMethods for how these replace the client's former
// currency->locale and country->date-format guessing tables.
export interface RegionInterface {
  country: Country;
  currency: string;
  timezone: string;
  locales: string[];
  default_locale: string;
  number_locale: string;
  date_format: string;
  hour_cycle: 'h12' | 'h23';
  address_format: 'us' | 'international' | 'number_city_zip' | 'number_zip_city';
  distance_unit: 'km' | 'mi';
  legal_pack: string;
  limits: RegionLimitsInterface;
}

export interface CompanyDetailsInterface {
  // General & profile fields
  id: number;
  currency: string;
  timezone: string;
  /** Present whenever the API eager-loads the company's regional config — always true for the
   * primary company in initialData; may be absent on lighter-weight embeds (e.g. an order's
   * embedded company). Always guard with `?.` and fall back to country-keyed logic when absent. */
  region?: RegionInterface;
  payment_gateways: PaymentGateway[];
  spreedly_environment_key?: string | null;
  /** Whether the consumer may keep a card on file. True for every non-Spreedly company; for a
   * Spreedly company only when its gateway supports stored credentials. Gates the save-card
   * consent on the Spreedly card form, which requires it present and true — a payload without the
   * key predates the saved-cards work and cannot be assumed to have a gateway that retains
   * tokens, so the consent stays hidden rather than promising a card we may not be able to keep. */
  saved_cards_enabled?: boolean;
  stripe_id: string;
  /** The Stripe methods charged on the platform's account rather than on `stripe_id` (separate
   * charges and transfers). Absent from a backend that predates the field: see
   * `isStripeDirectPaymentMethod`. */
  stripe_platform_payment_methods?: PaymentMethod[];
  /** How the restaurant's Stripe charges are made. `destination`: on the partner's platform with
   * `on_behalf_of` the restaurant (`stripe_id`), so Stripe.js runs as the platform. `direct` (or a
   * backend that predates the field): on the restaurant's own account, as before. */
  stripe_charge_model?: 'destination' | 'direct';
  /** Delayed-notification types the Payment Element must never offer, as Stripe names them. The
   * backend puts the same list on the intent. */
  stripe_excluded_payment_method_types?: string[];
  stripe_hideable_payment_method_types?: string[];
  slug: string;
  name: string;
  email: null | string;
  phone_number: string;
  logo_url: null | string;
  pictures: string[];
  primary_color: null | string;
  instagram_link: string | null;
  website_link: string | null;
  impressum_link: string | null;
  /** The imprint a self-serve restaurant pasted itself; see service_provider.uses_own_impressum. */
  impressum_content?: string | null;
  legal_address: string | null;
  cuisine_types: CuisineType[] | null;
  legal_lang: string;
  // The storefront UI language the restaurant configured (distinct from
  // legal_lang, which is the contract language), and the languages currently
  // active platform-wide.
  language: string;
  supported_languages: string[];
  is_live: boolean;
  /** The partner's non-billable test restaurant: fully working, shown with a TEST badge. */
  is_test_restaurant?: boolean;
  has_storefront: boolean;
  live_from: string | null;
  /** How the restaurant came onto the platform. Absent on backends that predate the WLP analytics work. */
  restaurant_source?: null | RestaurantSource;
  /** Absolute shop URL of this company (`{franchise web_shop_url}/{slug}`), resolved by the backend. */
  web_shop_url?: string | null;

  // Tax fields
  tax_behaviour: TaxBehaviour;
  tax_percentage: number;

  // Payment method & checkout fields
  supported_payment_methods: PaymentMethod[];
  /** Offered methods a gift card cannot be bought with (Klarna through PPRO). */
  gift_card_unavailable_payment_methods?: PaymentMethod[];
  paypal_standalone?: { available: boolean; client_id: string | null };
  hidden_payment_methods: IMap<ORDER_METHODS[] | string[], PaymentMethod>;
  has_in_store_card_payments: boolean;
  overwrite_cash_payments_to_invoice: boolean;
  has_paypal_upcharge: boolean;
  has_tips: boolean;
  default_tip_percentage: number | null;

  // Location fields
  has_multiple_locations: boolean;
  locations: CompanyLocation[];
  location_name: null | string;
  address: string;
  city: string;
  state: string;
  country: Country;
  zip_code: null | string;
  lat: number;
  lon: number;

  // Google place fields
  google_rating: string;
  google_rates_count: number;
  google_places_id: string;

  // Custom messages fields
  scheduled_order_option_title: string | null;

  // General order setting fields
  has_live_orders: boolean;
  hidden_order_methods: null | ORDER_METHODS[];
  has_asap_orders: boolean;
  has_scheduled_orders: boolean;
  has_pre_order: boolean;
  scheduled_orders_time_slot_interval: number;
  max_pre_order_days: number;
  show_hero_image_with_offers: boolean;

  // Pickup fields
  has_pickup: boolean;
  average_order_preparation_time?: AverageOrderPreparationTimeInterface | null;
  last_order_gap_pickup: number;
  min_schedule_ahead_time_pickup: number;

  // Delivery fields
  has_delivery: boolean;
  delivery_enabled: boolean;
  delivery_mode: DeliveryMode;
  average_order_delivery_time?: AverageOrderPreparationTimeInterface | null;
  last_order_gap_delivery: number;
  min_schedule_ahead_time_delivery: number;

  // Room service fields
  has_room_service: boolean;

  //Schedule fields
  open_now: boolean;

  // Pickup schedule fields
  has_work_schedule: boolean;
  work_schedule: OpeningHoursInterface[];
  current_work_schedule?: OpeningHoursInterface | null;
  next_work_schedule?: OpeningHoursInterface | null;

  // Delivery schedule fields
  has_delivery_schedule: boolean;
  open_now_for_delivery: boolean;
  delivery_schedule: OpeningHoursInterface[];
  current_delivery_schedule?: OpeningHoursInterface | null;
  next_delivery_schedule?: OpeningHoursInterface | null;

  // Paused orders fields
  orders_paused_until?: string | null;
  orders_pause_reason?: ORDERS_PAUSED_REASON | null;

  // Schedule exceptions
  closed_from: string | null;
  closed_until: string | null;

  // Related resources
  service_provider: ServiceProvider;
  cashback_program?: null | CashbackProgram;
  google_review_program?: null | LoyaltyProgram;
  smart_pricing_deals: SmartPricingRule[];
  smart_pricing_rules: SmartPricingRule[];
  delivery_zones: DeliveryZone[];
  room_service_locations: RoomServiceLocation[];
  table_number_hidden: boolean;

  // Promocodes
  show_master_promo_code: boolean;

  offers: Offer[];
  business_profile: IBusinessProfile;
  impressum: IImpressum | null;
}

export interface IBusinessProfile {
  legal_name: string;
  owner_email: string;
  owner_phone_number: string;
  owner_first_name: string;
  owner_last_name: string;
  full_legal_address: string;
  full_invoice_address: string | null;
  tax_id: string;
}

// Impressum values resolved server-side: each field is the company's Impressum
// override when filled, otherwise the business-profile value falls through.
export interface IImpressum {
  legal_name: string | null;
  owner_first_name: string | null;
  owner_last_name: string | null;
  owner_email: string | null;
  owner_phone_number: string | null;
  tax_id: string | null;
  full_address: string | null;
}

// One franchise company's resolved Impressum, returned by GET /franchises/impressums
// so the Impressum page can list every company in the franchise rather than only the
// company currently held in Redux.
export interface ICompanyImpressum extends IImpressum {
  company_id: number;
  company_name: string;
  slug: string;
  /** What a self-serve restaurant pasted; absent on backends that predate it. */
  content?: string | null;
}

/**
 * One restaurant's generated legal document, or the instruction to keep rendering the
 * jurisdiction bundle this app already ships.
 *
 * `source` is the only field the caller has to branch on. It is `platform` for every
 * restaurant until its partner's feature flag is turned on, so this endpoint existing
 * changes nothing on its own.
 */
/**
 * How a legal document is spelled in a URL. The API answers with the snake_case form in
 * `ILegalDocument.document`; both spellings live here so the mapping is visible in one
 * place rather than inferred from three call sites.
 */
export type LegalDocumentSlug = 'terms-and-conditions' | 'privacy-policy' | 'cookie-policy';

export interface ILegalDocument {
  document: 'terms_and_conditions' | 'privacy_policy' | 'cookie_policy';
  source: 'platform' | 'generated';
  available: boolean;
  legal_pack: null | string;
  locale: null | string;
  title: null | string;
  version: null | string;
  effective_from: null | string;
  /** Rendered HTML fragment. Null whenever `source` is `platform`. */
  html: null | string;
}

export interface Offer {
  id: number;
  type: OfferTypeEnum;
  kind: OfferKindEnum;
  details: any;
  timezone: string;
  days_running: number[];
  time_running_starts_at?: string;
  time_running_ends_at?: string;
  starts_at: string;
  ends_at?: string;
  is_happy_hour: boolean;
  translations: OfferTranslations;
  image_url?: string;
  image_source?: 'ai_generated' | 'manual_upload' | 'none';
}

export interface OfferTranslations {
  en: OfferTranslation;
  de: OfferTranslation;
}

export interface OfferTranslation {
  title: string;
  description: string;
}

export interface SmartPricingRule {
  id: number;
  name: string;
  order_methods: ORDER_METHODS[];
  modifier_type: SmartPricingRuleModifierType;
  modifier_value: number;
  is_visible: boolean;
  is_global: boolean;
  image_url?: string;
}

export interface DeliveryZone {
  id: number;
  name: string;
  color: string;
  is_hidden: boolean;
  min_threshold: number;
  max_threshold: null | number;
  fee: number;
  geometry_type: DELIVERY_ZONE_GEOMETRY_TYPES;
  geometry_data: google.maps.CircleOptions | google.maps.PolygonOptions | string[];
}

export interface DeliveryQuote {
  id: number;
  company_id: number;
  provider: string;
  carrier: string;
  price: number;
  customer_price: number;
  pickup_estimate: string;
  dropoff_estimate: string;
  expires_at: string;
}

export interface RoomServiceLocation {
  id: number;
  type: RoomServiceLocationType;
  name: string;
  street_name: string;
  street_number: string;
  city: string;
  zip_code: string;
  table_number_hidden: boolean;
}

export interface LogoInterface {
  height: number;
  width: number;
  url: string;
}

export interface ServiceProvider {
  /** Absent on backends that predate the WLP analytics work. The `partner_id` analytics key. */
  id?: number;
  name: string;
  country: string;
  website: string;
  logo_url: null | string;
  circle_logo_url: null | string;
  sender_email: string;
  support_email: string;
  stripe_key: string;
  adyen_key: null | string;
  // The publishable half of the Spreedly credentials — safe to expose, the
  // secret never leaves the backend.
  spreedly_environment_key?: null | string;
  google_analytics_id: string;
  amplitude_api_key: null | string;
  /** The provider's own Mixpanel project token, when it has one. */
  mixpanel_api_key: null | string;
  /**
   * FoodAmigos' own Mixpanel project token, sent for every provider (public by design, like any
   * browser token). Used only when the provider has no `mixpanel_api_key` of its own. Absent on
   * backends that predate the WLP analytics work.
   */
  platform_mixpanel_token?: null | string;
  google_pay_merchant_id: null | string;
  is_master: boolean;
  /** Internal / test partner: its restaurants' analytics carry `is_test: true` so they can be filtered out. */
  is_test?: boolean;
  /** Whether guests may request an invoice at this provider's restaurants. */
  issues_end_customer_receipts: boolean;
  /**
   * Whether this provider's restaurants have their own generated T&C and privacy policy.
   *
   * Known before the document request resolves, which is the whole point: without it the
   * legal pages cannot distinguish "no document is coming" from "one has not arrived
   * yet", and every customer would see the platform's legal text before the
   * restaurant's own replaced it.
   */
  has_generated_legal_documents: boolean;
  /**
   * Whether this provider's restaurants publish only the imprint they pasted themselves
   * (company `impressum_content`) — nothing generated, and no link while it is empty.
   */
  uses_own_impressum?: boolean;
  /** OTP channels this provider offers; anything absent here is rejected by the backend. */
  otp_delivery_channels: OTP_DELIVERY_CHANNELS[];
}

export interface Promotion {
  id: number;
  reward: number;
  end_date: string;
}

export interface CashbackProgram extends Promotion {
  title: string | null;
  percentage: number;
  point_lifetime: number;
  welcome_bonus: null | number;
  welcome_bonus_image_url: string | null;
  mobile_app_signup_bonus: null | number;
  app_signup_bonus_image_url: string | null;
  min_spending_limit: number;
  max_spending_limit: number;
}

export interface LoyaltyProgram extends Promotion {
  reward_threshold: number;
  is_per_item: boolean;

  rewards: RewardInterface[];
}

export interface OpeningHoursInterface {
  open?: OpeningHoursScheduleInterface;
  close?: OpeningHoursScheduleInterface;
  timezone?: string;
}

export interface OpeningHoursScheduleInterface {
  day: number;
  hour: number;
  minute: number;
  timezone: string;
}

export interface AverageOrderPreparationTimeInterface {
  min?: number | null;
  max?: number | null;
}

export interface MenuInterface {
  id: number;
  name: string;
  is_active: boolean;
  schedule: OpeningHoursInterface[];
  sorting_order: null | number;

  category_ids: number[];
  smart_pricing_rules?: SmartPricingRule[];
}

export interface CategoryInterface<T = GenericProductInterface> {
  id: number;
  menu_id: number;
  type: CategoryType;
  name: string;
  description?: string;
  image_url: null | string;
  product_ids: number[];
  menu_ids: number[];
  products?: T[];
  schedule?: OpeningHoursInterface[] | null;
  smart_pricing_rules?: SmartPricingRule[];
}

export interface GenericProductInterface {
  id: number;
  name: string | ReactNode;
  description?: string;
  image_url?: string;
  thumbnail_url?: string;
}

export type OrderMethodPrices = {
  [method in ORDER_METHODS]: number;
};

export type NutritionInfo = {
  gtin: null | string;
  product_type: null | 'prepared' | 'packaged';
  packaging_material: null | string;
  brand_or_manufacturer: null | string;
  net_quantity: null | {
    value: null | number;
    unit: string;
  };
  base_price: null | {
    required: boolean;
    value_per_unit: null | number;
    unit: null | string;
  };
  alcohol_by_volume_percent: null | number;
  caffeine_mg_per_100ml: null | number;
  high_caffeine_warning_required: boolean;
  pfand?: number;
  ingredients_text: null | string;
  allergens_list: null | string[];
  additives_list: null | string[];
  nutrition_per_100: null | {
    energy_kj: null | string;
    energy_kcal: null | string;
    fat_g: null | string;
    saturates_g: null | string;
    carbohydrate_g: null | string;
    sugars_g: null | string;
    protein_g: null | string;
    salt_g: null | string;
  };
};

export interface ProductInterface {
  id: number;
  type: EntryType.Product;
  name: string;
  base_price: number;
  order_method_prices: null | OrderMethodPrices;
  description: string;
  image_url: string;
  thumbnail_url: string;
  is_discounted: boolean;
  is_available: boolean;
  is_available_for_pickup: boolean;
  is_available_for_delivery: boolean;
  identifiers: string[];
  popularity_rank?: number;
  likes_count: number;
  nutrition_info: null | NutritionInfo;

  category_ids: number[];
  modifier_group_ids: number[];
}

export type OrderLineItem = BasketLineItem & {
  id: number;
  type: EntryType.Product;
  name: string;
  original_price: number;
  actual_price: number;
  product_original_price: number;
  product_actual_price: number;
  price: number;
  discount: number;
  description: string;
  image_url: string;
  thumbnail_url: string;
  is_available: boolean;

  children?: OrderLineItem[];
  modifiers: Modifier[];
  category: LineItemCategoryInterface;
};

export type Modifier = BasketLineItemModifier & {
  id: number;
  type: EntryType;
  name: string;
  base_price: number;
  order_method_prices: null | OrderMethodPrices;
  is_available: boolean;
  is_visible: boolean;
  is_available_for_pickup: boolean;
  is_available_for_delivery: boolean;
  nutrition_info: null | NutritionInfo;

  modifier_group_ids?: number[];
  category_ids?: number[];
};

export interface LineItemCategoryInterface {
  id: number;
  name: string;
  sorting_order: number;
}

export interface ModifierId {
  id: number;
  type: EntryType;
}

export interface ModifierGroupInterface<T = Modifier | ProductInterface> {
  id: number;
  is_required: boolean;
  is_variant: boolean;
  is_visible: boolean;
  max_count: number;
  multiply: boolean;
  name: string;
  type: ModifierGroupType;
  modifier_ids: ModifierId[];
  modifiers?: T[];
}

export interface RewardInterface {
  id: number;
  cost: number;
  discount: number;
  is_available: boolean;
}

export interface PaymentOptionPayloadInterface {
  company_id: number;
  payment_method_id: PaymentMethod;
  external_payment_method_id: string | stripeJs.PaymentMethod;
}

export interface PaymentOptionPropertiesInterface {
  id: number;
  payment_method_id: PaymentMethod;
  external_payment_method_id: null | string;
  payment_method_brand: null | PaymentMethodBrand;
  payment_method_details: null | string;
  name: string;
  display_name: string;
  is_default: boolean;
  is_saved: boolean;
  /** Gateway that holds the stored credential. Saved options are already scoped to the
   * company's gateway server-side; this is informational. */
  payment_gateway?: PaymentGateway;
}

export interface EventHelperActionInterface {
  type: EventHelperTypes;
  payload?: unknown;
}

export interface RewardInterface {
  id: number;
  cost: number;
  discount: number;
  is_available: boolean;
}

export interface Delivery {
  id: number;
  status: DeliveryStatus;
  carrier: string;
  runner_name: null | string;
  runner_phone_number: null | string;
  tracking_url: null | string;
  dropoff_estimate: null | string;
}

export type OrderFulfillmentEvent = {
  id: number;
  status: ORDER_FULFILLMENT_STATUSES;
  created_at: string;
};

export type OrderFulfillment = {
  id: number;
  number: number;
  status: ORDER_FULFILLMENT_STATUSES;
  estimated_preparation_time: number;
  initial_confirmation_time: string;
  estimated_pickup_time: string;
  created_at: string;
  complete_at: string;

  events: OrderFulfillmentEvent[];
};

export interface CustomerInfo {
  customer_phone_number: string;
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
}

export type GenericOrder<T = Record<any, any>> = {
  id: number | string;
  company_id: number;
  uuid: string;
  payment_status: ORDER_PAYMENT_STATUSES;
  payment_state?: ORDER_PAYMENT_STATES;
  sub_total: number;
  total: number;
  is_receipt_required: boolean;
  paid_at?: string;
  user?: UserInterface;
  payment: null | Payment;
  rejection_reason?: string;
} & CustomerInfo &
  InvoiceDto &
  T;

export type GiftCardOrder = GenericOrder & {
  id: string;
  uuid: string;
  company_id: number;
  order_number: number;
  gift_cards: string[];
};

export type Order = InvoiceData & {
  id: number;
  company_id: number;
  uuid: string;
  payment_status: ORDER_PAYMENT_STATUSES;
  payment_state?: ORDER_PAYMENT_STATES;
  delivery_provider: DeliveryProvider;
  coupon_type?: COUPON_TYPES;
  sub_total: number;
  tip: number;
  method_fee: number;
  payment_method_upcharge_fee: number;
  service_fee: number;
  total_discount: number;
  additional_tax: number;
  total: number;
  online_payment_total: number;
  cash_payment_total: number;
  is_receipt_required: boolean;
  scheduled_for: null | string;
  paid_at: null | string;

  method: ORDER_METHODS;
  customer_phone_number: string;
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
  room_service_details: null | string;

  fulfillment: null | OrderFulfillment;
  user?: UserInterface;
  coupon_details?: CouponDetailsInterface;
  line_items: OrderLineItem[];
  payment: null | Payment;
  delivery_quote?: DeliveryQuote;
  delivery?: Delivery;
  room_service_location?: RoomServiceLocation;
  rates: OrderRateInterface[];
  cashback_account_operations?: CashbackAccountOperation[];
  applied_offers?: Offer[];
  company: CompanyDetailsInterface;
};

export type Payment = {
  id: number;
  payment_method_id: PaymentMethod;
  payment_method_brand: null | PaymentMethodBrand;
  payment_method_details: null | string;
  /** Stripe's own type when `payment_method_id` is `stripe_other` (e.g. `revolut_pay`). */
  stripe_payment_method_type?: null | string;
  amount: number;
  created_at: string;
};

export type Refund = {
  id: number;
  amount: number;
  created_at: string;
};

export type InvoiceDto = {
  invoice_company_name?: string;
  invoice_first_name?: string;
  invoice_last_name?: string;
  invoice_address?: string;
  invoice_zip_code?: string;
  invoice_city?: string;
  invoice_tax_id?: string;
  invoice_email?: string;
};

export type InvoiceData = InvoiceDto & {
  delivery_street_name: null | string;
  delivery_street_number: null | string;
  delivery_city: null | string;
  delivery_state: null | string;
  delivery_country: null | string;
  delivery_zip_code: null | string;
  delivery_dropoff_action: null | DropoffAction;
  delivery_doorbell_name: null | string;
  delivery_company_name: null | string;
  delivery_lat: null | number;
  delivery_lng: null | number;
  customer_note: null | string;
};

export type OrderData = Partial<Omit<Order, 'line_items' | 'applied_offers'>> & {
  customer_distinct_id?: string;
  // Cookie-banner consent; the backend skips Meta CAPI and anonymises Mixpanel order_placed without it.
  tracking_consent?: boolean;
  company_id?: number;
  payment_method_id?: PaymentMethod;
  // Why this method was sent (see resolveCheckoutPaymentMethod). Logged by the backend only, never validated against a list.
  payment_method_source?: PaymentMethodSource;
  payment_intent_id?: string;
  /** The Payment Element checkout: the ConfirmationToken the Payment Element or the Express Checkout
   * Element produced. Present, the backend reads the method, the wallet and the save consent from it
   * on the restaurant's account, creates the intent at order time and expects no `payment_intent_id`;
   * the shop then confirms the intent with the same token. */
  stripe_confirmation_token?: string;
  paypal_order_id?: string;
  delivery_quote_id?: number;
  room_service_location_id?: number;

  room_service_details?: string;
  promo?: string | null;

  should_save_payment_info?: boolean;
  cashback_points?: number | null;
  marketing_consent?: boolean;
  adyen_payment_data?: null | PaymentData;
  return_url?: null | string;
  checkout_session_id?: string;
  spreedly_payment_method_token?: string | null;
  // Opaque Spreedly.ThreeDS.serialize() blob — required once the company's
  // gateway carries an SCA provider.
  spreedly_browser_info?: string | null;

  line_items: BasketLineItem[];
  delivery_zone?: DeliveryZone;
  room_service_location?: RoomServiceLocation;
  utm?: Partial<UTMInterface> | null;
  applied_offers?: Pick<Offer, 'id'>[];
  gift_card_code?: string;
};

export interface CashbackAccount {
  balance: number;
  has_mobile_app_signup_bonus: boolean;
  scheduled_expiration: null | {
    value: number;
    scheduled_for: string;
  };
}

export interface CashbackUpcomingExpiration {
  points: number;
  expires_at: string;
}

export interface CashbackAccountStats {
  lifetime_points: number;
}

export interface CashbackHistoryOperation {
  value: number;
  source: CashbackAccountOperationSource;
  date: string;
  order?: { number: string | null; total: number } | null;
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
  from?: number | null;
  to?: number | null;
  path?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
  links?: Record<string, string | null>;
}

export interface CashbackAccountOperation {
  id: number;
  value: number;
  created_at: string;
}

export interface OrderRateInterface {
  id: number;
  user_id: number;
  user?: UserInterface;
  rate: number;
}

export enum ButtonTypes {
  primary = 'primary',
  secondary = 'secondary',
  third = 'third',
  forth = 'forth',
  fifth = 'fifth',
  sixth = 'sixth',
  disabled = 'disabled',
  danger = 'danger',
  warning = 'warning',
}

export enum ButtonSizes {
  normal = 'normal',
  small = 'small',
}

export interface LoginResponseInterface {
  auth_token: string;
  user: ProfileInterface;
  bonus?: LoginBonus;
}

export interface LoginBonus {
  amount: number;
  kind: LoginBonusKind;
}

// Error body returned by /login when OTP verification fails (see backend OtpVerificationFailed).
export interface LoginErrorResponseData {
  message?: string;
  error_code?: OTP_VERIFICATION_FAILURE_REASON;
}

export interface OneTimePasswordInterface {
  password?: string;
  expires_at: string;
  delivery_channel: OTP_DELIVERY_CHANNELS;
  delivery_address: string;
  phone_number: string;
}

export interface ProfileInterface extends DeliveryAddressDataInterface {
  id?: number | null;
  phone_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  birthday?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  address?: string | null;
  zip_code?: string | null;
  lang?: string | null;
  timezone?: string | null;

  is_marketing_subscriber?: boolean;

  invoice_company_name?: string;
  invoice_first_name?: string;
  invoice_last_name?: string;
  invoice_address?: string;
  invoice_zip_code?: string;
  invoice_city?: string;
  invoice_tax_id?: string;
  invoice_email?: string;
}

export interface DeliveryAddressDataInterface {
  delivery_street_name?: string | null;
  delivery_street_number?: string | null;
  delivery_city?: string | null;
  delivery_state?: string | null;
  delivery_country?: string | null;
  delivery_zip_code?: string | null;
  delivery_dropoff_action?: DropoffAction | null;
  delivery_doorbell_name?: string | null;
  delivery_company_name?: string | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  customer_note?: string | null;
}

export interface CouponDetailsInterface {
  id?: number;
  code: string;
  referral_code_id?: number;
  discount_type: PROMO_CODE_DISCOUNT_TYPES;
  discount: number;
  type: PROMO_CODE_TYPES;
  expires_at: string;
  components: PromoComponentInterface[];
}

export interface PromoComponentInterface {
  id: number;
  discount: number;
  usage_threshold: number;
  max_discount?: number | null;
  is_used?: boolean;
}

export interface PromotionCodeResponseInterface {
  data: PromotionCodeDataInterface[];
  links: {
    first: string;
    last: string;
    prev: string;
    next: string;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginatedLinkInterface[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface PaginatedLinkInterface {
  url: string;
  label: string;
  active: boolean;
}

export interface PromotionCodeDataInterface {
  id: number;
  code: string;
  type: PROMO_CODE_TYPES;
  discount?: number;
  discount_type: PROMO_CODE_DISCOUNT_TYPES;
  expires_at: string;
  components: PromoComponentInterface[];
}

export interface PaymentIntent {
  id: string;
  client_secret: string;
  /** Stripe: the connected account the intent lives on, null for the platform's. Absent from a
   * backend that predates the field. */
  stripe_account?: string | null;
  /** Spreedly: the transaction token, and its state as Spreedly reported it. */
  token?: string;
  state?: string;
  /** Spreedly offsite purchase: the step the customer takes next. */
  offsite?: SpreedlyOffsiteNextAction;
}

/**
 * The step the backend's 402 names for an offsite purchase (`payment_intent.offsite`), built
 * server-side from the Spreedly transaction.
 */
export interface SpreedlyOffsiteNextAction {
  type: 'redirect' | 'stripe_payment_element' | 'braintree_buttons' | 'qr';
  checkout_url?: string | null;
  client_secret?: string | null;
  publishable_key?: string | null;
  apm_type?: string | null;
  transaction_token?: string | null;
  environment_key?: string | null;
  method?: string | null;
  qr_payload?: string | null;
  qr_image_url?: string | null;
  expires_at?: string | null;
}

export interface InitialDataResponseInterface {
  franchise: Franchise;
  company: CompanyDetailsInterface;
  profile: ProfileInterface;
  payment_options: PaymentOptionPropertiesInterface[];
}

export interface CompanyMenusData {
  menus: MenuInterface[];
  categories: IMap<CategoryInterface>;
  products: IMap<ProductInterface>;
  modifier_groups: IMap<ModifierGroupInterface>;
  modifiers: IMap<Modifier>;
}

export interface PusherConfig {
  key?: string;
  base_url?: string;
  cluster?: string;
}

export interface CompanyHoursHookInterface {
  open: boolean;
  openWholeDay: boolean;
  currentSchedule: OpeningHoursInterface | null;
  closedFrom: Moment;
  closedUntil: Moment;
  pickupClosed: boolean;
  deliveryClosed: boolean;
  orderTimeAvailable: boolean;
  opensToday: CompanyOpeningStatusInterface;
  opensStartingTomorrow: CompanyOpeningStatusInterface;
  ordersPausedUntil: string | null;
  orderArePausedTillEndOfDay: boolean;
  ordersPausedReason: ORDERS_PAUSED_REASON;
  ordersArePaused: boolean;
  ordersArePausedManually: boolean;
}

export interface CompanyOpeningStatusInterface {
  status: boolean;
  time: moment.Moment | null;
}

export interface FeedbackLabelInterface {
  message: string;
  icon?: ReactElement;
  type?: FeedbackLabelTypes;
  errors?: null | object;
  hideIcon?: boolean;
  className?: string;
  textClassname?: string;
}

export interface OrderMethodModalProps {
  openModal: boolean;
  orderMethod?: ORDER_METHODS;
  isScheduledModalOpen?: boolean;
  company?: CompanyDetailsInterface;
  showReturn?: boolean;
}

export interface OtpVerificationModalProps {
  openModal: boolean;
  phoneNumber?: string;
  /** `rememberDevice`: the guest ticked "remember this device" next to the code. */
  onSubmit?: (password: string, rememberDevice: boolean) => Promise<boolean>;
  /**
   * Fired when the user dismisses the modal without submitting (X button,
   * backdrop tap, etc.) so the outer flow (e.g. Adyen onSubmit) can settle
   * its pending action.
   */
  onCancel?: () => void;
}

export interface RestaurantClosedModalProps {
  openModal: boolean;
  onClose?: () => void;
}

export interface OrderBasketModalProps {
  openModal: boolean;
  onClose?: () => void;
}

export interface OfferModalProps {
  openModal: boolean;
  offer: Offer;
  onClose?: () => void;
}

export interface FreeItemsModalProps {
  openModal: boolean;
  offer: Offer;
  onClose?: () => void;
}

export interface MobileAppSignupBonusModalProps {
  openModal: boolean;
  onClose?: () => void;
}

export interface GenericModalProps {
  openModal: boolean;
  onClose?: () => void;
}

export interface GiftCard {
  id: number;
  franchise_id: number;
  balance: number;
  value: number;
  code: string;
  email: string;
}

export interface GiftCardBasketItem extends Pick<GiftCard, 'value' | 'email'> {
  quantity: number;
}

export interface GlobalAppContextMenuInterface {
  screenPosition: number;
  activeCategoryId: number;
  menuLoaded: boolean;
}

export interface ReducerDispatchActionPayloadInterface<Type, Payload> {
  type: Type;
  payload?: Payload;
}

export interface AvailableLanguageInterface {
  language: string;
  languageName: string;
}

export interface RoutesInterface {
  path: string;
  element?: ReactElement;
  protectedType?: 'Auth';
  children?: RoutesInterface[];
  loader?: ReactElement;
}

export interface GoogleLocationInterface<Type> {
  success: boolean;
  location: Type | null;
  /** ISO alpha-2 of the resolved address, when the lookup produced one. */
  country?: null | string;
  /** Postcode of the resolved address, when the lookup produced one. */
  postalCode?: null | string;
}

export interface GoogleAddressParamsInterface {
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  state?: string;
  country?: string;
  /** Prefers results inside this area without excluding the rest. */
  bounds?: google.maps.LatLngBoundsLiteral;
}

export interface ScheduledOrderSlotInterface {
  start_time: string;
  end_time: string;
}

export interface ErrorHandlerInterface {
  [error_code: string | number]: (error?: RenderableErrorResponse) => void;
}

export interface CreateOrderRateInterface {
  rate: number;
}

export interface GoogleReviewStatusInterface {
  id: number;
  status: GOOGLE_REVIEW_STATUS_TYPES;
  name: string;
  screenshot_url: string;
  created_at: string;
}

export interface ActionPayloadInterface<Type> {
  payload: Type;
  type: string;
}

export interface MenuItemModalProps {
  openModal: boolean;
  product: Partial<ProductInterface> | null;
  lineItem?: BasketLineItem | null;
  screenPosition?: number;
  onCloseModal?: () => void;
  onBack?: () => void;
  hasHeader?: boolean;
  basketItemIndex?: number;
  showProductAddedToast?: boolean;
  offer?: Offer;
}

export interface LoginModalProps {
  openModal: boolean;
  isStatic?: boolean;
  title?: string | ReactElement;
  initialPhoneNumber?: string;
  onLoginSuccess?: () => void;
  onCloseRedirect?: (isLoggedIn: boolean) => void;
}

export interface SearchDeliveryZoneParams {
  company_id: number;
  address?: null | string;
  zip?: null | string;
  lat?: null | number;
  lng?: null | number;
  throw?: boolean;
}

export interface CreateDeliveryQuotesParams {
  dropoff_time: null | string | Moment;
  street_name: string;
  street_number: string;
  city: string;
  state: null | string;
  country: string;
  zip_code: string;
  lat: null | number;
  lng: null | number;
  instructions?: null | string;
}

export interface GeolocationInterface {
  lat: number;
  lng: number;
}

export interface FormatCurrencyOptions {
  currency: string;
  locale: string;
  trimInteger?: boolean;
}

export interface UTMInterface {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_tracking_link_id: string | null;
  gclid: string | null;
  fbclid: string | null;
}

export interface MobileAppDeviceInterface {
  os: DeviceOS;
  brand: string;
  designName: string;
  deviceName: string;
  deviceType: string;
  isDevice: boolean;
  manufacturer: string;
  modelId: string;
  modelName: string;
  osVersion: string;
  platformApiLevel: number;
  productName: string;
}

export interface MobileAppWalletInterface {
  isApplePaySupported: boolean;
  isGooglePaySupported: boolean;
}

export interface MobileAppEdgeInsetsInterface {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface IMobileApp extends IMobileAppBase {
  id: string;
  splash_screen_icon: string;
  splash_screen_background_color: string;
  eas_project_id: string;
  google_service: string;
  google_service_info: string;
  play_store_service_account: string;
  apple_id: string;
  apple_team_id: string;
}

export interface IMobileAppFranchise extends ConfigInterface {
  id: number;
  uuid: string;
  web_shop_url: string;
}

export interface IMobileAppData extends IMobileApp {
  intro_screen_images: string[];
}

export interface IMobileAppInitialData {
  app: IMobileAppData;
  config: ConfigInterface;
}

export interface MobileAppConfigInterface {
  device: MobileAppDeviceInterface | null;
  wallets: MobileAppWalletInterface;
  insets: MobileAppEdgeInsetsInterface;
  session: string | null;
  featureFlags: Record<FeatureFlags, boolean>;
  data: IMobileAppInitialData | null;
}

export interface MobileAppContextInterface extends MobileAppConfigInterface {
  isMobileApp: boolean;
  isIos: boolean;
  isAndroid: boolean;
  pushNotificationPopUpOpen: boolean;
  pushNotificationsStatus: PushNotificationStatus;
  setPushNotificationPopUpOpen: (open: boolean) => void;
  hasFeature: (feature: FeatureFlags) => boolean;
}

export interface OnlineShopWebViewMessageInterface {
  type: OnlineShopWebViewMessageTypes;
  payload: any;
}

export interface OnlineShopWebViewPaymentResponseInterface {
  order: Order;
  payment_result: PaymentResult;
}

export interface PaymentResult {
  status: PaymentStatus;
  error: any;
  result: null | PaymentIntent;
}

export interface PaymentSetupIntentPayload {
  company_id: number;
  payment_methods: PaymentMethod[];
}

export interface PaymentSetupIntent {
  client_secret: string;
}

export interface IButtonStyle {
  background: string;
  text: string;
  border: string;
}
